import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {SEO} from "@/components/SEO.tsx";
import React from "react";

const Terms = () => {
  return (
    <div className="min-h-screen bg-background mt-12">
      <SEO
          title="Terms"
          description="Read the terms and conditions for using the platform"
          type="website"
          name="blankpoll"
          imageUrl="/blankpoll-banner.jpg"
      />
      <Navigation />
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Tabs defaultValue="user" className="w-full">
          <TabsList className="w-full md:w-fit mb-8">
            <TabsTrigger value="user" className="flex-1 md:flex-none">User Agreement</TabsTrigger>
            <TabsTrigger value="partner" className="flex-1 md:flex-none">Partner Agreement</TabsTrigger>
          </TabsList>

          <TabsContent value="user" className="prose prose-gray max-w-none">
            <h1 className="text-3xl font-bold mb-8">User Agreement and Terms of Use</h1>

            <h2 className="text-2xl font-semibold mt-8 mb-4">1. Introduction</h2>
            <p className="mb-4">
              This User Agreement ("Agreement") sets out the terms and conditions for using the services provided by BlankPoll ("Company") through the website blankpoll.com ("Platform"). By accessing or using the Platform, you agree to be bound by this Agreement.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">2. User Registration and Account Security</h2>
            <p className="mb-4">
              Users must provide accurate, current, and complete information when registering on the Platform.
            </p>
            <p className="mb-4">
              Users are responsible for maintaining the security of their accounts. Any unauthorized access must be reported to the Company immediately.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">3. Poll Usage Terms</h2>
            <p className="mb-4">
              Users may participate in polls according to the specified rules.
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>Some polls may allow users to vote only once. The Company reserves the right to prevent multiple votes.</li>
              <li>In certain polls, users may change their votes. However, the Company retains the right to restrict vote modifications for specific polls.</li>
              <li>The Company has the right to change the format, rules and voting conditions of polls at any time.</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">4. Rewards</h2>
            <p className="mb-4">
              Some polls may offer rewards, but this is not guaranteed for all polls.
            </p>
            <p className="mb-4">
              Users who correctly predict poll results must provide complete and accurate information within the specified time. Otherwise, the Company may cancel the reward or transfer it to the next eligible user.
            </p>
            <p className="mb-4">
              The points obtained from the polls are activated within two weeks at the latest. Points can be withdrawn for incorrect transactions. The company always has the authority to make savings in order to create a fair system in this regard.
            </p>
            <p className="mb-4">
              The Company reserves the right to modify or remove the reward system at any time.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">5. Removal of Polls</h2>
            <p className="mb-4">
              The Company has the right to remove polls from the Platform without providing any reason.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">6. User Obligations and Prohibitions</h2>
            <p className="mb-4">
              Users must use the Platform in compliance with laws and ethical standards.
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>Users may not share false, misleading, offensive, or rights-infringing content.</li>
              <li>Spam, fraudulent voting, or manipulative activities are prohibited on the Platform.</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">7. Data Security and Privacy</h2>
            <p className="mb-4">
              User data is protected in accordance with applicable data protection regulations.
            </p>
            <p className="mb-4">
              Users agree that their data may be anonymized and analyzed by the Company.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">8. Service Content and Changes</h2>
            <p className="mb-4">
              The Company reserves the right to change the content, services, and terms of use of the Platform at any time.
            </p>
            <p className="mb-4">
              The Company may suspend or terminate access to the Platform temporarily or permanently.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">9. Limitation of Liability</h2>
            <p className="mb-4">
              The Company does not guarantee that the Platform will function without errors or interruptions.
            </p>
            <p className="mb-4">
              The Company is not responsible for disputes between users.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">10. Termination and Account Closure</h2>
            <p className="mb-4">
              Users may close their accounts at any time.
            </p>
            <p className="mb-4">
              The Company reserves the right to suspend or delete accounts that violate this Agreement.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">11. Dispute Resolution</h2>
            <p className="mb-4">
              Any disputes arising from this Agreement shall be resolved under the jurisdiction of the courts in United Kingdom, London.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">12. Effectiveness and Changes</h2>
            <p className="mb-4">
              This Agreement takes effect upon publication. The Company reserves the right to update the terms at any time.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">13. Contact Information</h2>
            <p className="mb-4">
              For any questions or feedback, please contact us at support@blankpoll.com.
            </p>
          </TabsContent>

          <TabsContent value="partner" className="prose prose-gray max-w-none">
            <h1 className="text-3xl font-bold mb-8">Partner Agreement</h1>

            <p className="mb-4">
              This Partner Agreement ("Agreement") is entered into as of the Effective Date by and between [blankpoll] ("Platform") and the partner identified in the applicable Order Form or Registration Process ("Partner").
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">1. DEFINITIONS</h2>
            <p className="mb-4">1.1 Platform: The services and tools provided by the Platform, including but not limited to polling, analytics, and engagement solutions.</p>
            <p className="mb-4">1.2 Partner: A business, brand, startup, or influencer that subscribes to the Platform to create and publish polls, gain insights, and engage with users.</p>
            <p className="mb-4">1.3 Fees: The payments made by the Partner to the Platform for access to and use of the Platform.</p>
            <p className="mb-4">1.4 Subscription Term: The period during which the Partner has access to the Platform as per the applicable plan and can create and publish polls on the Platform.</p>
            <p className="mb-4">1.5 Effective Date: The date on which the Partner agrees to the terms of this Agreement.</p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">2. SERVICES PROVIDED</h2>
            <p className="mb-4">2.1 The Platform grants the Partner the right to create and publish polls on the Platform during the Subscription Term.</p>
            <p className="mb-4">2.2 The Platform will provide necessary support and maintenance services as per the selected plan.</p>
            <p className="mb-4">2.3 The Partner may use the Platform only for its intended business purpose and in compliance with all applicable laws.</p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">3. FEES, PAYMENTS & REFUNDS</h2>
            <p className="mb-4">3.1 The Partner agrees to pay all fees as per the agreed pricing structure.</p>
            <p className="mb-4">3.2 All payments are due in full upon invoicing and must be made within the specified period.</p>
            <p className="mb-4">3.3 No Refund Policy: Fees for the current billing period are non-refundable. However, if the Partner cancels before the renewal date, they will not be charged for future periods.</p>
            <p className="mb-4">3.4 Failure to make payments may result in the removal of published polls or suspension of access to the Platform.</p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">4. CANCELLATION & TERMINATION</h2>
            <p className="mb-4">4.1 The Partner may cancel their subscription at any time by providing written notice. Cancellation will be effective at the end of the current billing cycle.</p>
            <p className="mb-4">4.2 The Platform may terminate this Agreement immediately if the Partner breaches any terms of this Agreement.</p>
            <p className="mb-4">4.3 The Platform reserves the right to modify or terminate polls at any time with reasonable prior notice.</p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">5. DATA & PRIVACY</h2>
            <p className="mb-4">5.1 The Platform will take reasonable measures to ensure data security and privacy.</p>
            <p className="mb-4">5.2 The Partner acknowledges and agrees that data collected through the Platform may be used for analytics and service improvement.</p>
            <p className="mb-4">5.3 Both parties agree to comply with applicable data protection laws.</p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">6. CONFIDENTIALITY</h2>
            <p className="mb-4">6.1 Both parties agree to keep any confidential information shared under this Agreement secure and not disclose it to third parties.</p>
            <p className="mb-4">6.2 The Platform shall not be held liable for any direct, indirect, incidental, consequential, or punitive damages, including but not limited to financial loss, reputational damage, or other harm suffered by the Partner or any third party arising from the use of the Platform.</p>
            <p className="mb-4">6.3 If the Partner's activities cause any damage (financial, reputational, or otherwise) to the Platform, the Partner shall be fully responsible for compensating all incurred losses, including legal interest and any additional costs.</p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">7. GOVERNING LAW & DISPUTE RESOLUTION</h2>
            <p className="mb-4">7.1 This Agreement shall be governed by and construed in accordance with the laws of United Kingdom.</p>
            <p className="mb-4">7.2 Any disputes arising out of or in connection with this Agreement shall be resolved through arbitration in London, United Kingdom.</p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">8. MISCELLANEOUS</h2>
            <p className="mb-4">8.1 This Agreement constitutes the entire understanding between the parties.</p>
            <p className="mb-4">8.2 The Platform may update this Agreement with prior notice to the Partner.</p>
            <p className="mb-4">8.3 If any provision of this Agreement is found invalid, the remaining provisions shall remain in effect.</p>

            <p className="mt-8 mb-4">The parties have executed this Agreement as of the Effective Date.</p>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
};

export default Terms;
